import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UUID } from "crypto";
import api from "@/app/api";
import { buyingPatternsKeys } from "../keys/buyingPatterns";
import type {
  PartnerAsk,
  PartnerAskCreate,
  PartnerAskUpdate,
  PartnerAskProductMapping,
  PartnerAskProductMappingCreate,
  PartnerAskProductMappingUpdate,
} from "@/app/api/types/buyingPatterns";

// Partner Ask queries
export function usePartnerAsks(partnerId: UUID | null) {
  return useQuery({
    queryKey: buyingPatternsKeys.partnerAsks(partnerId!),
    queryFn: () => api.buyingPatterns.getPartnerAsks(partnerId!),
    enabled: !!partnerId,
  });
}

export function useAskProducts(askId: UUID | null) {
  return useQuery({
    queryKey: buyingPatternsKeys.askProducts(askId!),
    queryFn: () => api.buyingPatterns.getAskProducts(askId!),
    enabled: !!askId,
  });
}

// Partner Ask mutations
export function useCreatePartnerAsk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      partnerId,
      data,
    }: {
      partnerId: UUID;
      data: PartnerAskCreate;
    }) => api.buyingPatterns.postPartnerAsk(partnerId, data),
    onSuccess: (newAsk) => {
      queryClient.invalidateQueries({
        queryKey: buyingPatternsKeys.partnerAsks(newAsk.partner_id),
      });
    },
  });
}

export function useUpdatePartnerAsk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ askId, data }: { askId: UUID; data: PartnerAskUpdate }) =>
      api.buyingPatterns.putAsk(askId, data),
    onSuccess: (updatedAsk) => {
      queryClient.invalidateQueries({
        queryKey: buyingPatternsKeys.partnerAsks(updatedAsk.partner_id),
      });
    },
  });
}

export function useDeletePartnerAsk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (askId: UUID) => api.buyingPatterns.deleteAsk(askId),
    onSuccess: (deletedAsk) => {
      queryClient.invalidateQueries({
        queryKey: buyingPatternsKeys.partnerAsks(deletedAsk.partner_id),
      });
      queryClient.invalidateQueries({
        queryKey: buyingPatternsKeys.askProducts(deletedAsk.id),
      });
    },
  });
}

// Product Mapping mutations
export function useCreateProductMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      askId,
      data,
    }: {
      askId: UUID;
      data: PartnerAskProductMappingCreate;
    }) => api.buyingPatterns.postAskProduct(askId, data),
    onSuccess: (newMapping) => {
      queryClient.invalidateQueries({
        queryKey: buyingPatternsKeys.askProducts(newMapping.partner_ask_id),
      });
    },
  });
}

export function useUpdateProductMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      mappingId,
      data,
    }: {
      mappingId: UUID;
      data: PartnerAskProductMappingUpdate;
    }) => api.buyingPatterns.putProductMapping(mappingId, data),
    onSuccess: (updatedMapping) => {
      queryClient.invalidateQueries({
        queryKey: buyingPatternsKeys.askProducts(updatedMapping.partner_ask_id),
      });
    },
  });
}

export function useDeleteProductMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mappingId: UUID) =>
      api.buyingPatterns.deleteProductMapping(mappingId),
    onSuccess: (deletedMapping) => {
      queryClient.invalidateQueries({
        queryKey: buyingPatternsKeys.askProducts(deletedMapping.partner_ask_id),
      });
    },
  });
}
